import { Namespace, TFunction } from "i18next";

import { BusinessPersonalDetailsFormFields } from "@/components/Onboarding/Steps/PersonalDetails/PersonalDetailsFormFields/PersonalDetailsFormFields";
import {
  BasicCustomerData,
  SimCustomerInfo,
  SimCustomerInfoAddESIMNotAllowedReasonItem,
} from "@/services/model";
import { getCountryFromId } from "@/utils/countries";
import { getLocalizedDateString } from "@/utils/dateHelpers";

export const getSalutationTranslationKey = (
  salutation: BasicCustomerData["salutation"],
) => {
  switch (salutation?.id) {
    case 1:
      return "Onboarding.sections.personal-details.form.formOfAddress.mr";
    case 2:
      return "Onboarding.sections.personal-details.form.formOfAddress.mrs";
    default:
      return "Onboarding.sections.personal-details.form.formOfAddress.other";
  }
};

export const getPhoneFromBasicCustomerDataPhone = (
  phone: BasicCustomerData["phone"] | undefined,
) => {
  if (!phone) {
    return undefined;
  }

  if (phone.countryCode && phone.areaCode && phone.cli) {
    return `${phone.countryCode} ${phone.areaCode} ${phone.cli}`;
  }

  // After a local save the object only has phone.phone (slashes used as separators).
  return phone.phone ? phone.phone.replaceAll("/", " ") : undefined;
};

export const getLocalizedBirthday = (
  birthday: string | undefined,
  language: string,
) => {
  // Ensure we have a valid date string
  if (!birthday || typeof birthday !== "string" || birthday.trim() === "") {
    return "-";
  }

  // Try to parse the date safely
  const date = new Date(birthday);
  if (isNaN(date.getTime())) {
    return "-";
  }

  return getLocalizedDateString(birthday, language);
};

export const getLanguageId = (
  t: TFunction<Namespace, undefined, Namespace>,
  baseLanguage: string,
) => {
  switch (baseLanguage) {
    case t("translation:Common.languageCodes.deCode"):
      return 1;
    case t("translation:Common.languageCodes.enCode"):
      return 2;
    case t("translation:Common.languageCodes.frCode"):
      return 3;
    default:
      return 2;
  }
};

export const transformPersonalDetailsFormToBasicCustomerData: (
  formValues: BusinessPersonalDetailsFormFields,
  data: BasicCustomerData,
  language: string,
  t: TFunction<Namespace, undefined, Namespace>,
) => BasicCustomerData = (formValues, data, language, t) => {
  const birthday = formValues.dateOfBirth ?? data.birthday;

  return {
    id: data?.id,
    customerId: data.customerId,
    salutation: {
      id:
        Number(formValues.formOfAddress) === 0
          ? 0
          : Number(formValues.formOfAddress) || data.salutation!.id,
    },
    phone: {
      id: data.phone?.id,
      phone: formValues.phone.replaceAll(" ", "/"),
    },
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    language: { id: getLanguageId(t, language) },
    ...(birthday ? { birthday: toValidBirthdayFormat(birthday) } : {}),
    mainAddress: {
      id: data.mainAddress?.id,
      zip: formValues.zip,
      city: formValues.city,
      street: formValues.street,
      houseNumber: formValues.houseNumber,
      postalDeliveryNotes: formValues.postalDeliveryNotes,
      country: { id: formValues?.country?.ID },
    },
    ...(!!data.companyName || !!formValues.organizationName
      ? {
          companyName: formValues.organizationName || data.companyName,
          legalForm: formValues.organizationForm,
        }
      : {}),
    ...(!!formValues.organizationId && {
      registerNo: formValues.organizationId,
    }),
    ...(!!data.customerType && {
      customerType: data.customerType,
    }),
  };
};

export const transformBasicCustomerDataToPersonalDetailsForm: (
  data: BasicCustomerData,
) => Omit<BusinessPersonalDetailsFormFields, "terms"> = (data) => {
  return {
    formOfAddress: data.salutation?.id?.toString() || "0",
    firstName: data?.firstName || "Max",
    lastName: data?.lastName || "Mustermann",
    street: data?.mainAddress?.street || "",
    houseNumber: data?.mainAddress?.houseNumber || "",
    postalDeliveryNotes: data?.mainAddress?.postalDeliveryNotes || "",
    zip: data?.mainAddress?.zip || "",
    city: data?.mainAddress?.city || "",
    country: getCountryFromId(data?.mainAddress?.country.id),
    phone: data?.phone?.phone?.replaceAll("/", "") || "+41",
    dateOfBirth: data.birthday
      ? (() => {
          try {
            const date = new Date(data.birthday);
            if (isNaN(date.getTime())) {
              return "";
            }

            return date.toISOString().substring(0, 10);
          } catch (error) {
            return "";
          }
        })()
      : "",
    organizationName: data?.companyName || "",
    organizationForm: data?.legalForm || { id: 0 },
    organizationId: data?.registerNo || "",
  };
};

// firstName is a mandatory field when filling personal details. If it's not
// there, the customer hasn't filled their personal details.
export const hasValidPersonalDetails = (customer: BasicCustomerData) =>
  !!customer.firstName &&
  customer.mainAddress?.street !== "Dummy" &&
  customer.mainAddress?.zip !== "0000";

export const isBusinessCustomer = (customer: BasicCustomerData) =>
  customer?.customerType?.id === 2;

export const canOnlyBookData = (customer: BasicCustomerData) =>
  !!customer.dataOnly;

export const canAddESim = (simCustomerActions?: SimCustomerInfo): boolean =>
  simCustomerActions !== undefined &&
  simCustomerActions?.addESIMAllowed === true;

export const cannotAddEsimBecauseIDCheckOpen = (
  simCustomerActions?: SimCustomerInfo,
) =>
  simCustomerActions?.addESIMNotAllowedReason?.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.IDCheckOpen,
  ) || false;
export const cannotAddEsimBecauseReachedMaximumSims = (
  simCustomerActions?: SimCustomerInfo,
): boolean =>
  simCustomerActions?.addESIMNotAllowedReason?.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.TooManySIMs,
  ) || false;

export const cannotAddEsimBecauseBillDunning = (
  simCustomerActions?: SimCustomerInfo,
): boolean =>
  simCustomerActions?.addESIMNotAllowedReason?.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.DunningLevel3,
  ) || false;

// manual payments level 4 in the dunning process
export const cannotAddEsimBecauseDunningProcessOpen = (
  simCustomerActions?: SimCustomerInfo,
): boolean =>
  simCustomerActions?.addESIMNotAllowedReason?.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.DunningProcessOpen,
  ) || false;

const toValidBirthdayFormat = (birthday: string): string => {
  // Ensure we have a valid date string
  if (
    typeof birthday !== "string" ||
    birthday.trim() === "" ||
    birthday === "-"
  ) {
    return "";
  }

  // If already in YYYY-MM-DD format, return as is
  if (birthday.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return birthday;
  }

  // Parse the date safely
  const date = new Date(birthday);
  if (isNaN(date.getTime())) {
    return "";
  }

  // Format as YYYY-MM-DD using fr-CA locale (which uses YYYY-MM-DD format)
  return date.toLocaleDateString("fr-CA");
};

// Birthdate is valid if
// - entered date is an actual date (better safe than sorry)
// - date is before now
// - date is no longer ago then 120 years
export const validateBirthDate = ({
  value,
  t,
}: {
  value: string;
  t: TFunction;
}) => {
  if (!value || typeof value !== "string") {
    return t("translation:label.validation.invalidBirthdate");
  }

  // Ensure the date is in YYYY-MM-DD format for HTML5 date input
  if (!value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return t("translation:label.validation.invalidBirthdate");
  }

  const date = new Date(value);
  date.setHours(12, 0, 0, 0); // avoid timeshift by different timezone settings between frontend and backend

  if (isNaN(date.getTime())) {
    return t("translation:label.validation.invalidBirthdate");
  }

  if (Date.now() < date.getTime()) {
    return t("translation:label.validation.invalidBirthdate");
  }

  if (new Date().setFullYear(new Date().getFullYear() - 120) > date.getTime()) {
    return t("translation:label.validation.invalidBirthdate");
  }

  return true;
};
